// modules/asset/frontend/pages/AssetDetail.tsx
//
// Asset detail + edit page — E-2's frontend deliverable. Reachable
// via /dashboard/assets/:id (route declared in
// modules/asset/frontend/routes.tsx). Linked from AssetList rows.
//
// Edit model is "single-form, save-all" — the page always shows
// inputs (not a separate edit mode); Save submits a PATCH with the
// fields whose current value differs from the loaded baseline.
// Rationale: the form is short (5 editable fields), and a separate
// edit toggle adds clicks without value at this scale.
//
// Status transitions to retired / missing are treated as "edit"
// rather than a separate destructive action. They still write
// asset.updated audit; we just colour the Save button red when the
// user picks retired/missing so it doesn't feel like a routine edit.

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Boxes, Save } from "lucide-react";
import { apiFetch } from "@nexus/frontend-shared/api-client";
import PageHeader from "@nexus/frontend-shared/components/PageHeader";
import Skeleton from "@nexus/frontend-shared/components/Skeleton";

interface AssetRow {
  id: string;
  tenant_id: string;
  site_id: string | null;
  name: string;
  kind: string;
  status: "active" | "retired" | "missing";
  description: string | null;
  serviced_at: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_LABEL: Record<AssetRow["status"], string> = {
  active: "啟用",
  retired: "已退役",
  missing: "失蹤",
};

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; baseline: AssetRow }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

export default function AssetDetail(): React.ReactElement {
  const { id = "" } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  // Editable form fields — initialised from baseline on load. Empty
  // string is the cleared state for nullable fields (description /
  // serviced_at / site_id); the PATCH handler treats empty string as
  // "set to NULL" intentionally so the form-side stays simple.
  const [name, setName] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState<AssetRow["status"]>("active");
  const [siteId, setSiteId] = useState("");
  const [description, setDescription] = useState("");
  const [servicedAt, setServicedAt] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setState({ kind: "loading" });
      try {
        const r = await apiFetch(`/v1/assets/${encodeURIComponent(id)}`);
        if (r.status === 404) {
          if (!cancelled) setState({ kind: "not_found" });
          return;
        }
        if (!r.ok) {
          if (!cancelled) setState({ kind: "error", message: `HTTP ${r.status}` });
          return;
        }
        const row = (await r.json()) as AssetRow;
        if (cancelled) return;
        setState({ kind: "ready", baseline: row });
        setName(row.name);
        setKind(row.kind);
        setStatus(row.status);
        setSiteId(row.site_id ?? "");
        setDescription(row.description ?? "");
        setServicedAt(row.serviced_at ?? "");
      } catch (err) {
        if (cancelled) return;
        setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  async function handleSave(): Promise<void> {
    if (state.kind !== "ready") return;
    setSaving(true);
    setSaveError(null);
    try {
      // Body sends ALL editable fields; the server-side PATCH only
      // writes columns that actually changed (and the diff audit row
      // reflects only those). Cheap belt-and-suspenders.
      const body = {
        name,
        kind,
        status,
        site_id: siteId,
        description,
        serviced_at: servicedAt,
      };
      const r = await apiFetch(`/v1/assets/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        let detail = "";
        try { detail = JSON.stringify(await r.json()); } catch { /* ignore */ }
        setSaveError(`HTTP ${r.status}${detail ? `：${detail}` : ""}`);
        return;
      }
      // Re-fetch so the baseline updates + updated_at is fresh.
      const fresh = await apiFetch(`/v1/assets/${encodeURIComponent(id)}`);
      const row = (await fresh.json()) as AssetRow;
      setState({ kind: "ready", baseline: row });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  if (state.kind === "loading") {
    return (
      <div>
        <PageHeader title="資產詳細" description="載入中…" />
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }
  if (state.kind === "not_found") {
    return (
      <div>
        <PageHeader title="資產不存在" />
        <p className="text-sm text-text-secondary">
          找不到 ID 為 <code className="font-mono">{id}</code> 的資產，可能已被刪除或屬於其他 tenant。
        </p>
        <button
          type="button"
          onClick={() => nav("/dashboard/assets")}
          className="mt-4 inline-flex items-center gap-1 text-2xs text-accent hover:underline"
        >
          <ArrowLeft className="h-3 w-3" /> 回資產列表
        </button>
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div>
        <PageHeader title="資產詳細" />
        <div className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-2xs text-danger">
          載入失敗：{state.message}
        </div>
      </div>
    );
  }

  const isDestructive = status === "retired" || status === "missing";
  const baseline = state.baseline;
  const dirty =
    name !== baseline.name ||
    kind !== baseline.kind ||
    status !== baseline.status ||
    siteId !== (baseline.site_id ?? "") ||
    description !== (baseline.description ?? "") ||
    servicedAt !== (baseline.serviced_at ?? "");

  return (
    <div>
      <PageHeader
        title={baseline.name}
        description={
          <>
            <span className="font-mono">{baseline.id}</span>
            <span className="mx-2">·</span>
            <span>建立於 {new Date(baseline.created_at).toLocaleString("zh-TW", { timeZone: "Asia/Taipei" })}</span>
            {baseline.updated_at !== baseline.created_at && (
              <>
                <span className="mx-2">·</span>
                <span>最後修改 {new Date(baseline.updated_at).toLocaleString("zh-TW", { timeZone: "Asia/Taipei" })}</span>
              </>
            )}
          </>
        }
        actions={
          <button
            type="button"
            onClick={() => nav("/dashboard/assets")}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-2xs text-text-secondary hover:bg-bg-hover"
          >
            <ArrowLeft className="h-3 w-3" /> 回列表
          </button>
        }
      />

      <div className="rounded-xl border border-border bg-bg-surface p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex flex-col gap-1 text-2xs">
            <span className="text-text-muted">名稱</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="px-2 py-1.5 rounded border border-border bg-bg-base text-text-primary focus:ring-1 focus:ring-accent focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-2xs">
            <span className="text-text-muted">種類</span>
            <input
              type="text"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              placeholder="如 fire_extinguisher"
              className="px-2 py-1.5 rounded border border-border bg-bg-base text-text-primary font-mono focus:ring-1 focus:ring-accent focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-2xs">
            <span className="text-text-muted">狀態</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as AssetRow["status"])}
              className="px-2 py-1.5 rounded border border-border bg-bg-base text-text-primary focus:ring-1 focus:ring-accent focus:outline-none"
            >
              <option value="active">{STATUS_LABEL.active}</option>
              <option value="retired">{STATUS_LABEL.retired}</option>
              <option value="missing">{STATUS_LABEL.missing}</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-2xs">
            <span className="text-text-muted">案場 ID（留空 = tenant-wide）</span>
            <input
              type="text"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              placeholder="01J... 或留空"
              className="px-2 py-1.5 rounded border border-border bg-bg-base text-text-primary font-mono focus:ring-1 focus:ring-accent focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-2xs sm:col-span-2">
            <span className="text-text-muted">說明</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="px-2 py-1.5 rounded border border-border bg-bg-base text-text-primary focus:ring-1 focus:ring-accent focus:outline-none resize-y"
            />
          </label>
          <label className="flex flex-col gap-1 text-2xs sm:col-span-2">
            <span className="text-text-muted">最近檢查 / 服務時間（ISO 格式，例 2026-05-23T10:00:00Z；留空 = 未記錄）</span>
            <input
              type="text"
              value={servicedAt}
              onChange={(e) => setServicedAt(e.target.value)}
              placeholder="2026-05-23T10:00:00Z"
              className="px-2 py-1.5 rounded border border-border bg-bg-base text-text-primary font-mono focus:ring-1 focus:ring-accent focus:outline-none"
            />
          </label>
        </div>

        {saveError && (
          <div className="rounded border border-danger/30 bg-danger/5 px-3 py-2 text-2xs text-danger">
            儲存失敗：{saveError}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
          <div className="flex items-center gap-2 text-2xs text-text-muted">
            <Boxes className="h-3.5 w-3.5" />
            <span>asset module</span>
          </div>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={!dirty || saving}
            className={[
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-2xs font-medium",
              "disabled:opacity-40 disabled:cursor-not-allowed",
              isDestructive
                ? "bg-danger text-white hover:bg-danger/90"
                : "bg-accent text-white hover:bg-accent/90",
            ].join(" ")}
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? "儲存中…" : isDestructive ? `儲存（${STATUS_LABEL[status]}）` : "儲存"}
          </button>
        </div>
      </div>
    </div>
  );
}
